import { useCallback, useEffect, useRef, useState } from "react";
import { del, get, set } from "idb-keyval";
import { saveWorkspace } from "@/app/notebookQueries";
import type { Notebook, TaskResult } from "@/kernel/runner";

export const AUTOSAVE_MS = 800;
const key = (id: string) => `lms:workspace:${id}`;

type Queued = { notebook: Notebook; results: Record<string, TaskResult>; at: number };

export type SaveState = { kind: "saved"; at?: Date } | { kind: "pending" } | { kind: "saving" } | { kind: "offline"; since: Date } | { kind: "error"; message: string };

/** Автосохранение черновика: PUT через 800 мс после изменения; без сети —
 *  копия в IndexedDB и повтор при возвращении связи (E1 ERR_NETWORK). */
export function useAutosave(assignmentId: string, serverUpdatedAt: string | undefined) {
  const [state, setState] = useState<SaveState>({ kind: "saved", at: serverUpdatedAt ? new Date(serverUpdatedAt) : undefined });
  const [restored, setRestored] = useState<Queued | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const pending = useRef<Queued | null>(null);

  const flush = useCallback(async () => {
    const q = pending.current;
    if (!q) return true;
    setState({ kind: "saving" });
    try {
      const at = await saveWorkspace(assignmentId, q.notebook, q.results);
      if (pending.current === q) pending.current = null;
      await del(key(assignmentId)).catch(() => undefined);
      setState({ kind: "saved", at: new Date(at) });
      return true;
    } catch (e) {
      const offline = e instanceof TypeError || (typeof navigator !== "undefined" && navigator.onLine === false);
      await set(key(assignmentId), q).catch(() => undefined);
      setState(offline ? { kind: "offline", since: new Date(q.at) } : { kind: "error", message: e instanceof Error ? e.message : "не удалось сохранить" });
      return false;
    }
  }, [assignmentId]);

  const schedule = useCallback(
    (notebook: Notebook, results: Record<string, TaskResult>) => {
      const q = { notebook, results, at: Date.now() };
      pending.current = q;
      // Копия в IndexedDB сразу: перезагрузка в окне debounce не теряет правки.
      void set(key(assignmentId), q).catch(() => undefined);
      setState((s) => (s.kind === "offline" || s.kind === "error" ? s : { kind: "pending" }));
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => void flush(), AUTOSAVE_MS);
    },
    [flush, assignmentId],
  );

  // При открытии: локальная копия новее серверной — берём её и отправляем.
  useEffect(() => {
    let cancelled = false;
    get<Queued>(key(assignmentId))
      .then((q) => {
        if (cancelled || !q) return;
        const server = serverUpdatedAt ? Date.parse(serverUpdatedAt) : 0;
        if (q.at > server) {
          setRestored(q);
          pending.current = q;
          void flush();
        } else void del(key(assignmentId)).catch(() => undefined);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [assignmentId, serverUpdatedAt, flush]);

  useEffect(() => {
    const retry = () => void flush();
    window.addEventListener("online", retry);
    const iv = window.setInterval(() => {
      if (pending.current) void flush();
    }, 30_000);
    return () => {
      window.removeEventListener("online", retry);
      window.clearInterval(iv);
      window.clearTimeout(timer.current);
    };
  }, [flush]);

  return { state, schedule, flush, restored };
}
